import { default as React } from 'react';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { DocsHelpIcon } from './components';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  helperText: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
  },
  errorText: {
    color: theme.palette.error.main,
  },
}));

export const DescriptionField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  uiSchema,
  idSchema,
}: FieldExtensionComponentProps<string>) => {
  const classes = useStyles();
  const hasErrors = rawErrors && rawErrors.length > 0;
  const docsUrl = uiSchema?.['ui:options']?.docsUrl as string | undefined;

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Strip control characters (U+0000–U+001F, U+007F) that GitHub API rejects,
    // replacing newlines/tabs with a space to preserve word boundaries
    const sanitized = event.target.value
      .replace(/[\r\n\t]/g, ' ')
      .replace(/[^\x20-\x7E\u00A0-\uFFFF]/g, '');
    onChange(sanitized);
  };

  const id = idSchema?.$id;
  const label = schema.title || 'Description';
  const rows = (uiSchema?.['ui:options']?.rows as number) || 3;
  const maxLength = schema.maxLength;

  const getHelperText = () => {
    if (hasErrors) {
      return rawErrors?.join(', ');
    }
    if (maxLength) {
      return `${(formData || '').length}/${maxLength} characters`;
    }
    return undefined;
  };

  return (
    <FormControl fullWidth className={classes.root} error={hasErrors}>
      <Box className={classes.labelContainer}>
        <InputLabel shrink sx={{ position: 'relative', transform: 'none' }}>
          {label}
          {required && ' *'}
        </InputLabel>
        {docsUrl && (
          <DocsHelpIcon
            docsUrl={docsUrl}
            tooltipText="Description field documentation"
          />
        )}
      </Box>
      <TextField
        id={id}
        value={formData || ''}
        onChange={handleChange}
        required={required}
        error={hasErrors}
        multiline
        rows={rows}
        variant="outlined"
        inputProps={{
          maxLength: maxLength,
        }}
        helperText={getHelperText()}
      />

      {!hasErrors && schema.description && (
        <FormHelperText className={classes.helperText}>
          {schema.description}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const DescriptionFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'DescriptionField',
    component: DescriptionField,
  }),
);
